package com.ashes.dev.ai.weather.weathershow

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.coroutines.resume

@Serializable
private data class BigDataCloudResponse(
    val city: String? = null,
    val locality: String? = null,
    val principalSubdivision: String? = null,
    val countryName: String? = null
)

@Serializable
private data class PhotonResponse(
    val features: List<PhotonFeature>
)

@Serializable
private data class PhotonFeature(
    val geometry: PhotonGeometry
)

@Serializable
private data class PhotonGeometry(
    val coordinates: List<Double> // [longitude, latitude]
)

class JsLocationProvider : LocationProvider {
    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true })
        }
    }

    override suspend fun getCurrentLocation(): LocationData? = suspendCancellableCoroutine { continuation ->
        val geolocation = window.navigator.asDynamic().geolocation
        if (geolocation != null) {
            geolocation.getCurrentPosition(
                { position ->
                    val lat = position.coords.latitude as Double
                    val lon = position.coords.longitude as Double
                    continuation.resume(LocationData(lat, lon))
                },
                {
                    continuation.resume(null)
                }
            )
        } else {
            continuation.resume(null)
        }
    }

    override suspend fun getAddressFromLocation(latitude: Double, longitude: Double): String? {
        return try {
            val response: BigDataCloudResponse = client.get("https://api.bigdatacloud.net/data/reverse-geocode-client") {
                parameter("latitude", latitude)
                parameter("longitude", longitude)
                parameter("localityLanguage", "en")
            }.body()
            
            val city = response.city?.takeIf { it.isNotEmpty() } 
                ?: response.locality?.takeIf { it.isNotEmpty() }
                ?: response.principalSubdivision
            
            if (city != null) "$city, ${response.countryName ?: ""}".trim(',', ' ') else null
        } catch (e: Exception) {
            "Unknown Location"
        }
    }

    override suspend fun getCoordinatesFromAddress(address: String): LocationData? {
        return try {
            // Using Photon API as it supports CORS and works better in browsers
            val response: PhotonResponse = client.get("https://photon.komoot.io/api/") {
                parameter("q", address)
                parameter("limit", "1")
            }.body()
            
            response.features.firstOrNull()?.geometry?.coordinates?.let { coords ->
                if (coords.size >= 2) {
                    // Photon returns [longitude, latitude]
                    LocationData(latitude = coords[1], longitude = coords[0])
                } else null
            }
        } catch (e: Exception) {
            null
        }
    }
}

actual fun getLocationProvider(): LocationProvider = JsLocationProvider()
